import { Box, Skeleton } from "@mui/material";
import { produce } from 'immer';
import moment from "moment";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { updateDashboardFunc } from "../../../../../lib/helper";
import { Dashboard } from "../../../../../types/files";
import ObjectUtils from "../../../../../utils/ObjectUtils";
import MultiSelector from "../../../MultiSelector";
import useStyles from "../styles";

const TagsGroupCell: React.FC<{
    dashboard: Dashboard,
    tagGroup: string,
    tagsPerDashboard?: number,
    tags: string[],
    selections?: string[],
}> = ({ dashboard, tagGroup, tagsPerDashboard, tags, selections }) => {
    const classes = useStyles();
    const [updating, setUpdating] = useState<boolean>(false);
    const [tagsGroupLoaded, setTagsGroupLoaded] = useState<boolean>(false);
    const tagsGroupLoadedRef = useRef<boolean>(false);

    const handleSelectTags = useCallback(async (selections: string[], title: string) => {
        const lookUpTagGroup = dashboard.tagGroups?.find(dashboard => dashboard.title.toLowerCase() === title.toLowerCase());

        if (!ObjectUtils.equalObjects(lookUpTagGroup?.tags, selections)) {
            setUpdating(true);
            await updateDashboardFunc(produce(dashboard, (draft) => {
                if (!draft.tagGroups?.find(group => group.title.toLowerCase() === title.toLowerCase())) {
                    draft.tagGroups = [...(draft.tagGroups || []), { title, tags: selections }]
                        .map(({ title, tags }) => ({title, tags }));
                } else {
                    const groupIndex = draft.tagGroups.findIndex(group => group.title.toLowerCase() === title.toLowerCase());

                    draft.tagGroups[groupIndex].tags = selections;
                    draft.tagGroups = draft.tagGroups.map(({ title, tags }) => ({title, tags }));
                }
            })).then(() => setUpdating(false));
        }
    }, [dashboard]);

    useEffect(() => {
        setTagsGroupLoaded(tagsGroupLoadedRef.current);
    }, []);

    useEffect(() => {
        if (!selections?.length) {
            setTagsGroupLoaded(false);
            if (moment().diff(dashboard!.createdAt, 'seconds') < 60) {
                const timeOut = setTimeout(() => {
                    clearTimeout(timeOut);
                    setTagsGroupLoaded(true);
                    tagsGroupLoadedRef.current = true;
                }, 10000);
            } else {
                setTagsGroupLoaded(true);
                tagsGroupLoadedRef.current = true;
            }
        } else  {
            setTagsGroupLoaded(true);
            tagsGroupLoadedRef.current = true;
        }
    }, [dashboard, selections]);

    return (<>
        {tagsGroupLoaded ? (
            <MultiSelector
                mainOptions={tags}
                selections={selections}
                onSelection={(selections) => handleSelectTags(selections, tagGroup)}
                loading={updating}
                limit={tagsPerDashboard}
                withSearch
                cropped={260} />
        ) : (
            <Box sx={{ width: '100%' }}>
                <Skeleton animation="wave" className={classes.wave} />
            </Box>
        )}
    </>);
}

export default memo(TagsGroupCell);
